import React, { useCallback } from 'react';
import { AccountListItemRow } from '@/experiences/sales-portal/domain/accounts/screens/account-list/components/account-list-item-row';
import { AccountListRowData } from '@/experiences/sales-portal/domain/accounts/screens/account-list/types';
import { createStyles } from '@/contexts/theme-context';
import { FlatList } from 'react-native';
import {
  Separator,
  usePageLayoutBodyScroll,
  usePageLayoutHeaderInset,
} from '@usepepper/sprout-kit-ui';

interface AccountListMobileListProps {
  data: AccountListRowData[];
  emptyComponent: React.ReactElement;
  listFooter?: React.ReactElement | null;
  onEndReached?: () => void;
  onRowClick: (row: AccountListRowData) => void;
}

// Owns the page scroll and reports into PageLayout's pipeline (title collapse +
// bottom-nav hide). No bar-height inset: scrolling toward the list's end is what
// hides the bar (see SalesPortalTabBar).
export const AccountListMobileList: React.FC<AccountListMobileListProps> = ({
  data,
  emptyComponent,
  listFooter,
  onEndReached,
  onRowClick,
}) => {
  const [styles, theme] = useStyles();
  const { onScroll } = usePageLayoutBodyScroll();
  // Inside the scroll content so scrolling consumes the collapsing
  // large-title chrome's height (scroll={false} contract)
  const headerInset = usePageLayoutHeaderInset();

  const renderRow = useCallback(
    ({ item }: { item: AccountListRowData }) => (
      <AccountListItemRow
        lastFulfilledDisplay={item.lastFulfillmentDate?.display ?? '—'}
        onPress={() => onRowClick(item)}
        restaurantName={item.restaurantName}
        restaurantUUID={item.restaurantUUID}
        subtitle={
          item.accountId
            ? `${item.accountId} • ${item.restaurantAddress}`
            : item.restaurantAddress
        }
      />
    ),
    [onRowClick],
  );

  return (
    <FlatList
      contentContainerStyle={[
        styles.contentContainer,
        { paddingTop: headerInset },
      ]}
      data={data}
      ItemSeparatorComponent={() => (
        <Separator marginHorizontal={theme.sk.spacing.spaceMedium} />
      )}
      // Dismiss the search keyboard on scroll; "handled" keeps row taps working
      keyboardDismissMode="on-drag"
      keyboardShouldPersistTaps="handled"
      keyExtractor={keyExtractor}
      ListEmptyComponent={emptyComponent}
      ListFooterComponent={listFooter}
      onEndReached={onEndReached}
      onEndReachedThreshold={0.5}
      onScroll={onScroll}
      renderItem={renderRow}
      // Report often enough to drive the page's scroll-linked chrome (iOS
      // otherwise sends one scroll event per drag)
      scrollEventThrottle={16}
      style={styles.mobileList}
    />
  );
};

const keyExtractor = (item: AccountListRowData) => item.restaurantUUID;

const useStyles = createStyles((theme) => ({
  contentContainer: {
    paddingBottom: theme.sk.spacing.spaceLarge,
  },
  mobileList: {
    flex: 1,
  },
}));
